/**
 * m3 echo Frontend - Prompt Comparison Dialog
 *
 * Shows a detailed comparison table for all jobs of a selected prompt.
 * Displays scores, keyword contexts, and full LLM responses with highlighting.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useQueries } from '@tanstack/react-query';
import { jobsApi } from '../services/api';
import { useModelCatalog } from '../hooks/useModelCatalog';
import { JobDetail, JobResult } from '../types/api';
import { calculateMentionScore } from '../utils/mentionScore';

interface PromptComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  promptId: number | null;
  promptIndex: number | null;
  jobIds: number[];
  testNames: string[];
  searchTerm: string;
  searchTermSynonym1?: string | null;  // v4.8.0
  searchTermSynonym2?: string | null;  // v4.8.0
  breakdownData: Array<{
    test_name: string;
    job_id: number;
    created_at: string;
    [key: string]: any;
  }>;
}

const PromptComparisonDialog: React.FC<PromptComparisonDialogProps> = ({
  open,
  onClose,
  promptId,
  promptIndex,
  jobIds,
  testNames,
  searchTerm,
  searchTermSynonym1,  // v4.8.0
  searchTermSynonym2,  // v4.8.0
  breakdownData: _breakdownData,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { modelLabel } = useModelCatalog();

  const [jobDetails, setJobDetails] = useState<(JobDetail | null)[]>([]);
  const [collapsedColumns, setCollapsedColumns] = useState<Set<number>>(new Set());
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [availableModels, setAvailableModels] = useState<string[]>([]);

  // Toggle column collapse state
  const toggleColumn = (index: number) => {
    setCollapsedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Fetch all job details
  const jobQueries = useQueries({
    queries: jobIds.map((jobId) => ({
      queryKey: ['job', jobId],
      queryFn: async () => {
        const response = await jobsApi.getById(jobId);
        return response.data as JobDetail;
      },
      enabled: open && !!jobId,
      staleTime: 30000,
    })),
  });

  // Check if all queries are successful
  const allQueriesSuccess = jobQueries.every((q) => q.isSuccess);
  const queryDataKey = jobQueries.map((q) => q.data?.id).join(',');

  // Update job details when queries complete
  useEffect(() => {
    if (allQueriesSuccess) {
      const details = jobQueries.map((q) => q.data || null);
      setJobDetails(details);

      // Only models that actually produced a result for this prompt in
      // one of the compared tests - not every model any test ever used.
      const models = new Set<string>();
      details.forEach((job) => {
        if (job && job.results) {
          job.results.forEach((result) => {
            if (result.model_name && result.prompt_id === promptId) {
              models.add(result.model_name);
            }
          });
        }
      });

      const modelsList = Array.from(models).sort();
      setAvailableModels(modelsList);

      // Keep the selection if this prompt has it, otherwise fall back to the first model
      if (!modelsList.includes(selectedModel)) {
        setSelectedModel(modelsList[0] ?? '');
      }
    }
  }, [allQueriesSuccess, queryDataKey, promptId]);

  /**
   * Normalizes text by removing diacritics (accents) for comparison
   * e.g., "Telefónica" -> "Telefonica"
   */
  const normalizeText = (text: string): string => {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  /**
   * Highlights search terms in text (supports multiple terms for synonyms - v4.8.0)
   * Ignores accents/diacritics when matching (e.g., ó matches o)
   */
  const highlightKeywords = (text: string | null | undefined, keywords: string[]): React.ReactNode => {
    if (!text) return '-';

    // Filter out empty keywords
    const validKeywords = keywords.filter(k => k && k.trim());
    if (validKeywords.length === 0) return text;

    // Create normalized patterns for matching (ignores accents)
    const normalizedKeywords = validKeywords.map(k => normalizeText(k.toLowerCase()));
    const escapedKeywords = normalizedKeywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    // Find all matches in the normalized text
    const normalizedText = normalizeText(text);
    const regex = new RegExp(`(${escapedKeywords.join('|')})`, 'gi');

    // Build result by finding matches in normalized text and mapping back to original
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(normalizedText)) !== null) {
      // Add text before this match
      if (match.index > lastIndex) {
        result.push(<span key={`text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
      }
      // Add highlighted match (use original text, not normalized)
      const originalMatch = text.slice(match.index, match.index + match[0].length);
      result.push(
        <span
          key={`match-${match.index}`}
          style={{
            backgroundColor: '#ffeb3b',
            fontWeight: 'bold',
            padding: '2px 4px',
          }}
        >
          {originalMatch}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match
    if (lastIndex < text.length) {
      result.push(<span key={`text-${lastIndex}`}>{text.slice(lastIndex)}</span>);
    }

    return result.length > 0 ? <span>{result}</span> : text;
  };

  // Build search terms array for highlighting
  const allSearchTerms = [searchTerm, searchTermSynonym1, searchTermSynonym2].filter(Boolean) as string[];

  /**
   * Gets the result for a specific prompt and model from job results
   */
  const getPromptResult = (job: JobDetail | null, promptId: number, modelName: string): JobResult | null => {
    if (!job || !job.results) return null;

    // Find result matching both the prompt_id and model_name
    const result = job.results.find((r) => r.prompt_id === promptId && r.model_name === modelName);
    return result || null;
  };

  /**
   * Gets the actual score for a specific job, prompt, and model
   */
  const getScore = (job: JobDetail | null, promptId: number, modelName: string): number | null => {
    const result = getPromptResult(job, promptId, modelName);
    // No result, or a failed one: no score - not a 0.
    if (!result || result.error != null || !result.analysis_result) return null;

    return calculateMentionScore(
      result.analysis_result.mentioned,
      result.analysis_result.position
    );
  };

  /**
   * Text for a score cell: the score, "nicht auswertbar" for a failed
   * result, "-" when the model did not run this prompt in that test.
   */
  const getScoreLabel = (job: JobDetail | null, promptId: number, modelName: string): string => {
    const score = getScore(job, promptId, modelName);
    if (score !== null) return String(score);
    const result = getPromptResult(job, promptId, modelName);
    return result && result.error != null ? 'nicht auswertbar' : '-';
  };

  const isLoading = jobQueries.some((q) => q.isLoading);
  const hasError = jobQueries.some((q) => q.isError);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        Prompt {(promptIndex || 0) + 1} - Detaillierter Vergleich
      </DialogTitle>

      <DialogContent>
        {/* Model Selector */}
        {availableModels.length > 0 && (
          <Box sx={{ mb: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="model-selector-label">Modell</InputLabel>
              <Select
                labelId="model-selector-label"
                id="model-selector"
                value={selectedModel}
                label="Modell"
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {availableModels.map((model) => (
                  <MenuItem key={model} value={model}>
                    {modelLabel(model)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : hasError ? (
          <Alert severity="error">Fehler beim Laden der Job-Details</Alert>
        ) : !selectedModel ? (
          <Alert severity="info">Keine Modelldaten verfügbar</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '150px' }}>Metrik</TableCell>
                  {testNames.map((name, index) => {
                    const isCollapsed = collapsedColumns.has(index);
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 'bold',
                          minWidth: isCollapsed ? '40px' : '250px',
                          maxWidth: isCollapsed ? '40px' : 'auto',
                          padding: isCollapsed ? '4px' : '16px',
                          width: isCollapsed ? '40px' : 'auto',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => toggleColumn(index)}
                            sx={{ padding: '2px', minWidth: 'auto' }}
                          >
                            {isCollapsed ? <AddIcon fontSize="small" /> : <RemoveIcon fontSize="small" />}
                          </IconButton>
                          {!isCollapsed && <span>{name}</span>}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Row 1: Scores */}
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}
                  >
                    Bewertung
                  </TableCell>
                  {jobDetails.map((job, index) => {
                    const score = getScore(job, promptId!, selectedModel);
                    const isCollapsed = collapsedColumns.has(index);
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          backgroundColor:
                            score === null
                              ? '#bdbdbd'
                              : score >= 75
                              ? '#4caf50'
                              : score >= 50
                              ? '#8bc34a'
                              : score >= 25
                              ? '#ffc658'
                              : score >= 10
                              ? '#ff9800'
                              : '#f44336',
                          color: 'white',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          minWidth: isCollapsed ? '40px' : 'auto',
                          maxWidth: isCollapsed ? '40px' : 'auto',
                          width: isCollapsed ? '40px' : 'auto',
                          padding: isCollapsed ? '4px' : '16px',
                        }}
                      >
                        {!isCollapsed && getScoreLabel(job, promptId!, selectedModel)}
                      </TableCell>
                    );
                  })}
                </TableRow>

                {/* Row 2: Keyword Context */}
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}
                  >
                    Keyword-Kontext
                  </TableCell>
                  {jobDetails.map((job, index) => {
                    const result = getPromptResult(job, promptId!, selectedModel);
                    const context = result?.analysis_result?.context;
                    const isCollapsed = collapsedColumns.has(index);
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          maxWidth: isCollapsed ? '40px' : '250px',
                          minWidth: isCollapsed ? '40px' : 'auto',
                          width: isCollapsed ? '40px' : 'auto',
                          padding: isCollapsed ? '4px' : '16px',
                          overflow: 'hidden',
                        }}
                      >
                        {!isCollapsed && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {highlightKeywords(context, allSearchTerms)}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>

                {/* Row 3: Complete LLM Response */}
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', verticalAlign: 'top' }}
                  >
                    Vollständige LLM-Antwort
                  </TableCell>
                  {jobDetails.map((job, index) => {
                    const result = getPromptResult(job, promptId!, selectedModel);
                    const rawResponse = result?.raw_response;
                    const isCollapsed = collapsedColumns.has(index);
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          maxWidth: isCollapsed ? '40px' : '300px',
                          minWidth: isCollapsed ? '40px' : 'auto',
                          width: isCollapsed ? '40px' : 'auto',
                          padding: isCollapsed ? '4px' : '16px',
                          verticalAlign: 'top',
                          overflow: 'hidden',
                        }}
                      >
                        {!isCollapsed && (
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              maxHeight: '400px',
                              overflowY: 'auto',
                              fontSize: '0.85rem',
                            }}
                          >
                            {highlightKeywords(rawResponse, allSearchTerms)}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptComparisonDialog;
